// ============================================
// FILE: src/components/QRCodeScreen.jsx
// ============================================

import React, { useRef, useState } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { ArrowLeft, Copy, Share2, CheckCircle, Globe, Smartphone, Download } from 'lucide-react';

const APP_URL = process.env.REACT_APP_APP_URL || window.location.origin;
const APP_NAME = process.env.REACT_APP_NAME || 'Deal Store';

const QRCodeScreen = ({ onBack, showToast, appTheme = 'blue' }) => {
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);

  const headerStyle = appTheme === 'white'
    ? { background: '#fff', color: '#222', boxShadow: '0 2px 16px 0 rgba(0,0,0,0.04)' }
    : { background: 'var(--header-gradient)', color: 'var(--text-light)' };

  const handleCopy = () => {
    navigator.clipboard.writeText(APP_URL).then(() => {
      setCopied(true);
      if (showToast) showToast('Link copied!', 'success');
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: APP_NAME, text: 'Shop online at ' + APP_NAME, url: APP_URL });
      } catch (_) {}
    } else {
      handleCopy();
    }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = 'qr-website.png';
    a.click();
    if (showToast) showToast('QR code downloaded!', 'success');
  };

  return (
    <div className="min-h-screen pb-20" style={{ background: 'var(--primary-bg)', color: 'var(--text-main)' }}>
      {/* Header */}
      <div className="p-6 sticky top-0 z-10 shadow-lg" style={headerStyle}>
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            style={appTheme === 'white' ? { color: '#222' } : { color: 'var(--text-light)' }}
          >
            <ArrowLeft size={22} />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">QR Code</h1>
            <p className={appTheme === 'white' ? 'text-[#94969f] text-sm' : 'text-[#94969f] text-sm'}>
              Share our website with anyone
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto p-5">

        {/* QR Card */}
        <div className="bg-white rounded shadow-lg overflow-hidden mb-5">
          <div className="p-4 bg-[#ff3f6c] flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Globe size={20} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-white text-base">Website</p>
              <p className="text-xs text-white text-opacity-80">Scan to open in any browser</p>
            </div>
          </div>
          <div className="flex flex-col items-center p-6">
            {/* Visible SVG QR code */}
            <div className="p-4 bg-white rounded shadow-sm border border-gray-100">
              <QRCodeSVG
                value={APP_URL}
                size={200}
                level="H"
                marginSize={0}
                fgColor="#1a1a2e"
              />
            </div>
            {/* Hidden canvas used for download */}
            <div ref={canvasRef} className="hidden">
              <QRCodeCanvas
                value={APP_URL}
                size={400}
                level="H"
                marginSize={4}
                fgColor="#1a1a2e"
              />
            </div>
            <p className="mt-4 text-sm text-[#94969f] text-center break-all px-2">{APP_URL}</p>
            <p className="mt-1 text-xs text-[#94969f] text-center">Scan with your phone camera</p>
          </div>
        </div>

        {/* PWA hint card */}
        <div className="bg-white rounded shadow-lg overflow-hidden mb-5">
          <div className="p-4 bg-[#ff3f6c] flex items-center gap-3">
            <div className="p-2 bg-white bg-opacity-20 rounded-lg">
              <Smartphone size={20} className="text-white" />
            </div>
            <div>
              <p className="font-bold text-white text-base">Install as App</p>
              <p className="text-xs text-white text-opacity-80">Scan → open in browser → Add to Home Screen</p>
            </div>
          </div>
          <div className="flex flex-col items-center p-6">
            <div className="p-4 bg-white rounded shadow-sm border border-gray-100">
              <QRCodeSVG
                value={APP_URL}
                size={200}
                level="H"
                marginSize={0}
                fgColor="#7c3aed"
              />
            </div>
            <p className="mt-4 text-sm text-[#94969f] text-center break-all px-2">{APP_URL}</p>
            <p className="mt-1 text-xs text-[#94969f] text-center">Scan with your phone camera</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 mb-3">
          <button
            onClick={handleCopy}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded font-semibold border border-[#ff3f6c] text-[#ff3f6c] hover:bg-[#fff8fa] transition-all"
          >
            {copied ? <CheckCircle size={18} className="text-green-500" /> : <Copy size={18} />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>
          <button
            onClick={handleShare}
            className="flex-1 flex items-center justify-center gap-2 py-3 rounded font-semibold bg-[#ff3f6c] text-white hover:shadow-lg transition-all"
          >
            <Share2 size={18} />
            <span>Share</span>
          </button>
        </div>

        <button
          onClick={handleDownload}
          className="w-full flex items-center justify-center gap-2 py-3 rounded font-semibold bg-[#ff3f6c] text-white hover:shadow-lg transition-all"
        >
          <Download size={18} />
          <span>Download QR Code (PNG)</span>
        </button>

        <p className="text-center text-xs text-[#94969f] mt-5">
          Static QR file also available at <span className="font-mono">/qr-website.png</span>
        </p>
      </div>
    </div>
  );
};

export default QRCodeScreen;
